import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { iconRegistry, validGoogleSymbols } from "@pcln/horizon-icons";
import { useMemo } from "react";
import { CdnIcon } from "../CdnIcon/index.js";
import { HorizonIcon } from "../HorizonIcon/HorizonIcon.js";
import { spinnerVariants } from "./Spinner.variants.js";
const ALLOWED_SIZES = new Set([
    'md',
    'lg',
    'xl'
]);
const ICON_SIZE_MAP = {
    md: '24',
    lg: '48',
    xl: '64'
};
function isGoogleMaterialSymbol(iconName) {
    return validGoogleSymbols.includes(iconName);
}
function isCustomSvgIcon(iconName) {
    return iconName in iconRegistry;
}
const Spinner = ({ isLoading = true, size = 'md', emphasis = 'regular', iconName, gradientId = 'spinner-gradient-id', palette = 'primary' })=>{
    const { circleSlot, spinnerSlot, wrapperSlot, iconSlot } = spinnerVariants({
        size,
        emphasis,
        palette
    });
    const gradientColor = spinnerSlot();
    const iconElement = useMemo(()=>{
        if (!iconName || !ALLOWED_SIZES.has(size)) return null;
        const iconSize = ICON_SIZE_MAP[size] ?? '24';
        if (isGoogleMaterialSymbol(iconName)) return /*#__PURE__*/ jsx(CdnIcon, {
            className: iconSlot(),
            iconName: iconName,
            size: iconSize
        });
        if (isCustomSvgIcon(iconName)) return /*#__PURE__*/ jsx(HorizonIcon, {
            className: iconSlot(),
            name: iconName,
            size: iconSize
        });
        return null;
    }, [
        iconName,
        iconSlot,
        size
    ]);
    return /*#__PURE__*/ jsx(Fragment, {
        children: isLoading ? /*#__PURE__*/ jsxs("div", {
            className: `${wrapperSlot()} relative`,
            children: [
                /*#__PURE__*/ jsxs("svg", {
                    "data-testid": "spinner-svg",
                    className: "h-full w-full",
                    xmlns: "http://www.w3.org/2000/svg",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    children: [
                        /*#__PURE__*/ jsx("defs", {
                            children: /*#__PURE__*/ jsxs("linearGradient", {
                                id: gradientId,
                                x1: "0%",
                                y1: "0%",
                                x2: "0%",
                                y2: "100%",
                                children: [
                                    /*#__PURE__*/ jsx("stop", {
                                        offset: "0%",
                                        stopColor: gradientColor,
                                        stopOpacity: "0.001"
                                    }),
                                    /*#__PURE__*/ jsx("stop", {
                                        offset: "10%",
                                        stopColor: gradientColor,
                                        stopOpacity: "0.05"
                                    }),
                                    /*#__PURE__*/ jsx("stop", {
                                        offset: "20%",
                                        stopColor: gradientColor,
                                        stopOpacity: "0.06"
                                    }),
                                    /*#__PURE__*/ jsx("stop", {
                                        offset: "40%",
                                        stopColor: gradientColor,
                                        stopOpacity: "0.08"
                                    }),
                                    /*#__PURE__*/ jsx("stop", {
                                        offset: "50%",
                                        stopColor: gradientColor,
                                        stopOpacity: "0.02"
                                    }),
                                    /*#__PURE__*/ jsx("stop", {
                                        offset: "70%",
                                        stopColor: gradientColor,
                                        stopOpacity: "0.3"
                                    }),
                                    /*#__PURE__*/ jsx("stop", {
                                        offset: "95%",
                                        stopColor: gradientColor,
                                        stopOpacity: "0.9"
                                    }),
                                    /*#__PURE__*/ jsx("stop", {
                                        offset: "100%",
                                        stopColor: gradientColor,
                                        stopOpacity: "1"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ jsxs("g", {
                            children: [
                                /*#__PURE__*/ jsx("circle", {
                                    cx: "12",
                                    cy: "12",
                                    r: "10",
                                    stroke: circleSlot(),
                                    strokeWidth: "2.8"
                                }),
                                /*#__PURE__*/ jsx("circle", {
                                    cx: "12",
                                    cy: "12",
                                    r: "10",
                                    fill: "none",
                                    stroke: `url(#${gradientId})`,
                                    strokeDasharray: `${10 * Math.PI * 0.6} , ${10 * Math.PI * 2}`,
                                    strokeWidth: "2.8",
                                    strokeLinecap: "round",
                                    children: /*#__PURE__*/ jsx("animateTransform", {
                                        attributeName: "transform",
                                        type: "rotate",
                                        from: "0 12 12",
                                        to: "360 12 12",
                                        dur: "1.25s",
                                        repeatCount: "indefinite"
                                    })
                                })
                            ]
                        })
                    ]
                }),
                iconElement && /*#__PURE__*/ jsx("div", {
                    className: "absolute inset-0 flex items-center justify-center",
                    "data-testid": "icon-wrapper",
                    children: iconElement
                })
            ]
        }) : null
    });
};
export { Spinner };
